import { clamp, getPerspectiveCorners, PREVIEW_MAX_DIMENSION } from './perspectiveCropperUtils';

type ApplyPerspectiveParams = {
  cv: any;
  cvLoaded: boolean;
  naturalWidth: number;
  naturalHeight: number;
  baseCanvas: HTMLCanvasElement | null;
  baseMatRef: { current: any };
  previewMatRef: { current: any };
  previewSharesBaseRef: { current: boolean };
  transformScaleRef: { current: number };
  horizontalTilt: number;
  verticalTilt: number;
  targetCanvas: HTMLCanvasElement;
  quality: 'preview' | 'full';
};

type CreateBaseMatParams = {
  cv: any;
  cvLoaded: boolean;
  baseCanvas: HTMLCanvasElement | null;
  baseMatRef: { current: any };
  previewMatRef: { current: any };
  previewSharesBaseRef: { current: boolean };
};

type DrawPreviewParams = {
  previewCanvas: HTMLCanvasElement | null;
  cropRect: { x: number; y: number; width: number; height: number } | null;
  transformScale: number;
  rotation: number;
  sourceCanvas: HTMLCanvasElement;
};

export const createBaseMat = (params: CreateBaseMatParams) => {
  if (!params.cvLoaded || !params.cv || !params.baseCanvas) {
    return;
  }
  const fullMat = params.cv.imread(params.baseCanvas);
  params.baseMatRef.current = fullMat;
  const width = fullMat.cols;
  const height = fullMat.rows;
  const maxDim = Math.max(width, height);
  if (maxDim > PREVIEW_MAX_DIMENSION) {
    const scale = PREVIEW_MAX_DIMENSION / maxDim;
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));
    const previewMat = new params.cv.Mat();
    params.cv.resize(fullMat, previewMat, new params.cv.Size(targetWidth, targetHeight), 0, 0, params.cv.INTER_AREA);
    params.previewMatRef.current = previewMat;
    params.previewSharesBaseRef.current = false;
  } else {
    params.previewMatRef.current = fullMat;
    params.previewSharesBaseRef.current = true;
  }
};

export const applyPerspectiveToCanvas = (params: ApplyPerspectiveParams) => {
  if (!params.cvLoaded || !params.cv) {
    return;
  }

  if (!params.baseMatRef.current && params.baseCanvas) {
    createBaseMat({
      cv: params.cv,
      cvLoaded: params.cvLoaded,
      baseCanvas: params.baseCanvas,
      baseMatRef: params.baseMatRef,
      previewMatRef: params.previewMatRef,
      previewSharesBaseRef: params.previewSharesBaseRef,
    });
  }

  const sourceMat = params.quality === 'full'
    ? params.baseMatRef.current
    : params.previewMatRef.current || params.baseMatRef.current;
  if (!sourceMat) {
    return;
  }

  const w = sourceMat.cols;
  const h = sourceMat.rows;
  if (!w || !h) {
    return;
  }

  const scale = w / params.naturalWidth || 1;

  const hTilt = clamp(params.horizontalTilt, -1, 1);
  const vTilt = clamp(params.verticalTilt, -1, 1);

  const destCorners = getPerspectiveCorners({ width: w, height: h, hTilt, vTilt });

  const srcCoords = new Float32Array([
    0, 0,
    w, 0,
    w, h,
    0, h,
  ]);
  const dstCoords = new Float32Array(destCorners.flatMap((point) => [point.x, point.y]));

  const srcMat = params.cv.matFromArray(4, 1, params.cv.CV_32FC2, srcCoords);
  const dstMat = params.cv.matFromArray(4, 1, params.cv.CV_32FC2, dstCoords);
  const transformMatrix = params.cv.getPerspectiveTransform(srcMat, dstMat);

  const dst = new params.cv.Mat();
  params.cv.warpPerspective(
    sourceMat,
    dst,
    transformMatrix,
    new params.cv.Size(w, h),
    params.cv.INTER_LINEAR,
    params.cv.BORDER_CONSTANT,
    new params.cv.Scalar(),
  );

  params.targetCanvas.width = w;
  params.targetCanvas.height = h;
  params.cv.imshow(params.targetCanvas, dst);
  dst.delete();

  if (params.quality !== 'full') {
    params.transformScaleRef.current = scale;
  }

  srcMat.delete();
  dstMat.delete();
  transformMatrix.delete();
};

export const drawPreview = (params: DrawPreviewParams) => {
  if (!params.previewCanvas || !params.cropRect) {
    return;
  }

  const ctx = params.previewCanvas.getContext('2d');
  if (!ctx) {
    return;
  }

  const scaledX = params.cropRect.x * params.transformScale;
  const scaledY = params.cropRect.y * params.transformScale;
  const scaledWidth = params.cropRect.width * params.transformScale;
  const scaledHeight = params.cropRect.height * params.transformScale;

  ctx.save();
  ctx.clearRect(0, 0, params.previewCanvas.width, params.previewCanvas.height);
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, params.previewCanvas.width, params.previewCanvas.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  if (Math.abs(params.rotation) > 0.0001) {
    const rotationRad = (params.rotation * Math.PI) / 180;
    ctx.translate(params.previewCanvas.width / 2, params.previewCanvas.height / 2);
    ctx.rotate(rotationRad);
    ctx.drawImage(
      params.sourceCanvas,
      scaledX,
      scaledY,
      scaledWidth,
      scaledHeight,
      -params.previewCanvas.width / 2,
      -params.previewCanvas.height / 2,
      params.previewCanvas.width,
      params.previewCanvas.height,
    );
  } else {
    ctx.drawImage(
      params.sourceCanvas,
      scaledX,
      scaledY,
      scaledWidth,
      scaledHeight,
      0,
      0,
      params.previewCanvas.width,
      params.previewCanvas.height,
    );
  }

  ctx.restore();
};
